import React, { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { competitionService } from '../api/competitionsApi';
import { projectService } from '../../projects/api/projectsApi';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const defaultForm = {
  projectId: '',
  status: 'draft',
  feedback: '',
  notes: '',
  documents: [],
};

const inp = `w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:ring-2`;
const inpStyle = {
  borderColor: SYSTEM_COLORS.border,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};

const Label = ({ children }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
    {children}
  </label>
);

const ApplicationModal = ({ isOpen, onClose, application, competition }) => {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    setForm(application ? { ...defaultForm, ...application } : defaultForm);
  }, [application]);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingProjects(true);
    projectService.getProjects()
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load projects.'))
      .finally(() => setLoadingProjects(false));
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId) {
      toast.error('Project is required');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, competitionId: competition.id };
      if (application) {
        await competitionService.updateApplication(application.id, payload);
        toast.success('Application updated');
      } else {
        await competitionService.createApplication(payload);
        toast.success('Application created');
      }
      onClose();
    } catch {
      toast.error('Error saving application');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92dvh]"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface, boxShadow: SYSTEM_COLORS.shadowStrong }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                {application ? 'Edit Application' : 'New Application'}
              </h2>
              {competition && (
                <p className="text-xs mt-0.5 truncate max-w-[200px]" style={{ color: SYSTEM_COLORS.textSoft }}>
                  {competition.name}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:opacity-70"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <div className="p-5 space-y-4">
            <div>
              <Label>Project *</Label>
              <select
                name="projectId"
                value={form.projectId}
                onChange={handleChange}
                className={inp}
                style={inpStyle}
                required
              >
                <option value="">
                  {loadingProjects ? 'Loading projects…' : 'Select a project…'}
                </option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Status</Label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className={inp}
                style={inpStyle}
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="awarded">Awarded</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <Label>Feedback</Label>
              <textarea
                name="feedback"
                value={form.feedback}
                onChange={handleChange}
                rows={2}
                className={inp}
                style={inpStyle}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                className={inp}
                style={inpStyle}
              />
            </div>

            <div
              className="flex items-center gap-3 px-4 py-3 rounded-xl border"
              style={{
                borderColor: SYSTEM_COLORS.borderSoft,
                backgroundColor: SYSTEM_COLORS.surfaceSubtle,
              }}
            >
              <FileText className="w-4 h-4 flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
              <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                Document upload coming soon
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 px-5 py-4 border-t flex-shrink-0"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold border transition-opacity hover:opacity-70"
              style={{
                borderColor: SYSTEM_COLORS.border,
                color: SYSTEM_COLORS.textMuted,
                backgroundColor: SYSTEM_COLORS.shellSurface,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              {loading ? 'Saving…' : application ? 'Save Changes' : 'Create Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationModal;
